import type { CartAttribute } from "@/cart/cartAttributes";

/**
 * The pre-BF ad-to-page packages (Notion: "Pre-BF Offer: Ad-to-Page Landing
 * Pages + Bundle Rules"). Each hidden /lp page pre-selects one; "everyday"
 * is the Everyday page's, the Bag Pass without the brush roll, and "switch"
 * the Switch page's, the same Bag Pass until its own offer is set.
 */
export type LandingPackageId =
  | "essentials"
  | "pet"
  | "everyday"
  | "switch"
  | "multi-floor-dock"
  | "multi-floor-bag-pass";

/** The /lp pages, one per ad concept. */
export const LANDING_PAGE_IDS = [
  "pet",
  "everyday",
  "switch",
  "homefit",
  "offer",
] as const;
export type LandingPageId = (typeof LANDING_PAGE_IDS)[number];

/**
 * On the first Matic and every free line a landing page adds, naming the
 * package they came in with. The cart draws those lines as one group, and the
 * lp-discount Function frees the lines whose package matches the cart's.
 */
export const LANDING_PACKAGE_LINE_KEY = "_lp_package";

/** Cart attributes; private keys, so they reach the order but not checkout. */
export const LANDING_PACKAGE_CART_KEY = "_lp_package";

export const LANDING_PACKAGES: Record<
  LandingPackageId,
  { name: string; note: string; href: string }
> = {
  // /lp/offer's default bundle: the Bag Pass alone.
  essentials: {
    name: "Matic Essentials Bundle",
    note: "Annual Bag Pass included for the first year",
    href: "/lp/offer",
  },
  pet: {
    name: "Matic Pet Bundle",
    note: "Pet Brush Roll Upgrade + Annual Bag Pass included",
    href: "/lp/pet",
  },
  everyday: {
    name: "Matic Essentials Bundle",
    note: "Annual Bag Pass included for the first year",
    href: "/lp/everyday",
  },
  switch: {
    name: "Matic, Bag Pass package",
    note: "Annual Bag Pass included for the first year",
    href: "/lp/switch",
  },
  // /lp/offer's Multi-Floor package, by the visitor's pick.
  "multi-floor-dock": {
    name: "Matic Multi-Floor Bundle",
    note: "A second dock included",
    href: "/lp/offer",
  },
  "multi-floor-bag-pass": {
    name: "Matic Multi-Floor Bundle",
    note: "Annual Bag Pass included for the first year",
    href: "/lp/offer",
  },
};

export const LANDING_PACKAGE_IDS = Object.keys(
  LANDING_PACKAGES,
) as LandingPackageId[];

export const landingPackageLineAttributes = (
  id: LandingPackageId,
): CartAttribute[] => [{ key: LANDING_PACKAGE_LINE_KEY, value: id }];

export const getLandingPackageId = (line: {
  attributes?: readonly { key: string; value?: string | null }[] | null;
}): LandingPackageId | null => {
  const value = line.attributes?.find(
    (attribute) => attribute.key === LANDING_PACKAGE_LINE_KEY,
  )?.value;

  return LANDING_PACKAGE_IDS.find((id) => id === value) ?? null;
};

export const landingCartAttributes = (
  packageId: LandingPackageId | null,
): Record<string, string> =>
  packageId === null ? {} : { [LANDING_PACKAGE_CART_KEY]: packageId };

export const LANDING_PACKAGE_CART_COPY = {
  /**
   * Shown in the cart when a package sits alongside a discount or referral
   * code. TODO(discount-function): the Function's combination settings are
   * what actually keep the two apart at checkout; this only says so.
   */
  codeConflict: (packageName: string) =>
    `Codes can't be combined with ${packageName}. To use a code, remove it and add Matic from the shop page instead.`,
  /** How the code notice names each package. */
  packageNames: {
    essentials: "this bundle",
    pet: "this bundle",
    everyday: "this bundle",
    switch: "this bundle",
    "multi-floor-dock": "this bundle",
    "multi-floor-bag-pass": "this bundle",
  } satisfies Record<LandingPackageId, string>,
};

/** The package a cart holds alongside any discount code, or null. */
export const getLandingPackageCodeConflict = (
  cart:
    | {
        discountCodes?: readonly { code: string }[] | null;
        lines: {
          edges: readonly {
            node: Parameters<typeof getLandingPackageId>[0];
          }[];
        };
      }
    | null
    | undefined,
): LandingPackageId | null => {
  if ((cart?.discountCodes ?? []).length === 0) return null;

  for (const { node } of cart?.lines.edges ?? []) {
    const id = getLandingPackageId(node);
    if (id !== null) return id;
  }
  return null;
};
